import React, {useEffect} from 'react';
import AceEditor from 'react-ace';
import equal from 'react-fast-compare';

import {assert} from 'Asserts';
import {
  BenchlingAppType,
  ScalarDependencyTypeToValueTSType,
  MODEL_CONSTANT_BY_APP_CONFIG_TYPE,
  BenchlingAppSubtype,
  subtypeToModelTypeMapping,
} from 'benchling-apps/BenchlingAppTypes';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {getRegistrySupportedOligoAwareTypes, OligoAwareFolderItemType} from 'constants/RegistryConstants';
import FolderSelector from 'file-browser/FolderSelector';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {BioentitySearchInputWithItems} from 'inventory/BioentitySearchInput';
import deepCopy from 'lib/deepCopy';
import {EntrySearchInputWithItems} from 'notebook/entries/EntrySearchInput';
import {AssayRunSchemaSelectorIdInput} from 'notebook/integrations/runs/AssayRunSchemaSelector';
import RegistrySelector from 'registry/RegistrySelector';
import {RequestSchemaSelectorIdInput} from 'requests/creation/RequestSchemaSelector';
import {ResultSchemaSelectorIdInput} from 'results/ResultSchemaSelector';
import BoxSchemaSelector from 'samples/BoxSchemaSelector';
import ContainerSchemaSelector from 'samples/ContainerSchemaSelector';
import LocationSchemaSelector from 'samples/LocationSchemaSelector';
import PlateSchemaSelector from 'samples/PlateSchemaSelector';
import {StorableSchema} from 'samples/storables/StorableSchemaConstants';
import {StorableSearchInputWithItems} from 'samples/storables/StorableSearchInput';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import {Item} from 'ui-library/attach-dropdown/ItemSearchInput';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {IntermediateValueControlledNumericInput} from 'ui-library/inputs/ControlledNumericInput';
import {
  DATEPICKER_CUSTOM_INPUT_USER_EDIT_FORMAL_TIME_FORMATS,
  DATEPICKER_CUSTOM_INPUT_USER_EDIT_INFORMAL_TIME_FORMATS,
} from 'ui-library/inputs/DatetimeField';
import DatetimeInput, {DateInput} from 'ui-library/inputs/DatetimeInput';
import SecureTextInput from 'ui-library/inputs/SecureTextInput';
import TextInput from 'ui-library/inputs/TextInput';
import ProjectSelector from 'ui-library/ProjectSelector';
import {
  DATETIME_FORMATS,
  PRESENTATIONAL_LOCAL_DATETIME_FORMAT,
  PRESENTATIONAL_TIME_FORMAT,
} from 'util/DateConstants';
import {isValidDate, isValidDatetime} from 'util/DateUtil';
import {InputChangeEvent, NumericInputChangeEvent} from 'util/Types';
import uniqueId from 'util/uniqueId';
import {WorklistType} from 'worklists/WorklistConstants';
import {WorklistMultiSearchInput} from 'worklists/WorklistSearchInput';

import {getBenchlingAppConfigurationQuery} from './configuration/__generated/getBenchlingAppConfigurationQuery';
import BenchlingAppConfigurationArrayRow from './configuration/BenchlingAppConfigurationArrayRow';
import SchemaFieldSelectorInput from './configuration/parent-dependency/SchemaFieldSelectorInput';
import AppConfigurationProcessTaskSchemaSelector from './configuration/selector-components/AppConfigurationProcessTaskSchemaSelector';
import AppConfigurationProcessTaskStatusSelector from './configuration/selector-components/AppConfigurationProcessTaskStatusSelector';
import ScalarEnumSelectorInput from './configuration/selector-components/ScalarEnumSelectorInput';
import {
  ConfigValidationErrorTypes,
  DependencyConfigValidation,
  ValidationError,
  UpdateArrayElementValidationCallback,
} from './configuration/validation/ValidationTypes';

type ConfigArrayElement = NonNullable<
  ExtractByTypename<
    getBenchlingAppConfigurationQuery['benchlingApp'],
    'BenchlingAppNode'
  >['configuration'][number]['elements']
>[number];

type ConfigArrayElementDefinition = NonNullable<
  ExtractByTypename<
    getBenchlingAppConfigurationQuery['benchlingApp'],
    'BenchlingAppNode'
  >['configuration'][number]['elementDefinition']
>[number];

export type SupportedValueTypes = string | number | boolean | null;

export function getStringValueForDependency(val: SupportedValueTypes): string | null {
  if (val == null) {
    return val;
  }
  return val.toString();
}

export interface BenchlingAppEditorProps {
  type: BenchlingAppType;
  subtype: BenchlingAppSubtype | null;
  name: string;
  placeholder?: string;
  requiredConfig: boolean;
  value: SupportedValueTypes;
  elementDefinition: Array<ConfigArrayElementDefinition> | null;
  minElements?: number | null;
  maxElements?: number | null;
  scalarEnum?: Array<ScalarEnumOption> | null;
  elements: Array<ConfigArrayElement> | null;
  onChangeValue: (newVal: SupportedValueTypes) => void;
  onChangeElements: (newElements: Array<ConfigArrayElement>) => void;
  updateDependencyConfigValidation: (dependencyValidation: DependencyConfigValidation) => void;
  updateArrayElementValidation: UpdateArrayElementValidationCallback;
  dependencyValidation: DependencyConfigValidation;
  isEditable: boolean;
  schemaId: string | null;
}

export function singleValueToArrayValues<T>(id: T | null): Array<T> {
  return id ? [id] : [];
}

function arrayValuesToSingleValue<T>(ids: Array<T>): T | null {
  assert(ids.length <= 1, `App config supports selecting one or zero values, got ${ids.length} values`);
  return ids[0] ?? null;
}

/**
 * Helper HOC that transforms a single id value into an array of items to pass into a search input component
 */
function withSingleIdToSelectedItemsArray<
  WrappedProps extends {selectedItems: Array<Item>; onChange(newItems: Array<Item>): void},
>(
  WrappedComponent: React.ComponentType<WrappedProps>
): React.FunctionComponent<
  Omit<WrappedProps, 'selectedItems' | 'onChange'> & {id: string | null; onChange(newId: string | null): void}
> {
  return (props) => {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const {id, onChange, ...otherProps} = props;
    return (
      <WrappedComponent
        {...(otherProps as unknown as WrappedProps)}
        selectedItems={singleValueToArrayValues(id).map((itemId) => ({
          id: itemId,
        }))}
        onChange={(newItems) => {
          onChange(arrayValuesToSingleValue(newItems.map((item) => item.id!)));
        }}
      />
    );
  };
}

const WrappedBioentitySearchInput = withSingleIdToSelectedItemsArray(BioentitySearchInputWithItems);
const WrappedStorableSearchInput = withSingleIdToSelectedItemsArray(StorableSearchInputWithItems);
const WrappedEntrySearchInput = withSingleIdToSelectedItemsArray(EntrySearchInputWithItems);

const MAX_INT = 2147483647;
const MIN_INT = -2147483648;

function coerceEmptyStringToNullForSave(val: string): string | null {
  return val !== '' ? val : null;
}

function coerceNullToEmptyStringForDisplay(val: string | null): string {
  return val ?? '';
}

function createInvalidInputError(name: string, type: string, value: SupportedValueTypes): ValidationError {
  return {
    type: ConfigValidationErrorTypes.InvalidInputError,
    message: `${value} is not a valid ${type} value for ${name}.`,
  };
}

function getValidationErrors(
  props: Pick<
    BenchlingAppEditorProps,
    'name' | 'value' | 'requiredConfig' | 'type' | 'elements' | 'minElements' | 'maxElements'
  >
): Array<ValidationError> {
  const validationErrors: Array<ValidationError> = [];
  // Booleans always have a value of true or false, so requiredConfig shouldn't block if the boolean
  // is set to false.
  if (props.type === BenchlingAppType.boolean) {
    return validationErrors;
  }
  if (props.value != null) {
    if (props.type === BenchlingAppType.float && Number.isNaN(Number(props.value))) {
      validationErrors.push(createInvalidInputError(props.name, props.type, props.value));
    }
    if (props.type === BenchlingAppType.integer && !Number.isInteger(props.value)) {
      validationErrors.push(createInvalidInputError(props.name, props.type, props.value));
    }
    if (props.type === BenchlingAppType.datetime && !isValidDatetime({textValue: String(props.value)})) {
      validationErrors.push(createInvalidInputError(props.name, props.type, props.value));
    }
    if (props.type === BenchlingAppType.date && !isValidDate({textValue: String(props.value)})) {
      validationErrors.push(createInvalidInputError(props.name, props.type, props.value));
    }
  }

  if (props.type === BenchlingAppType.array) {
    if (props.minElements) {
      // If minElements was specified
      if (
        (props.elements == null && props.minElements > 0) ||
        (props.elements != null && props.elements.length < props.minElements)
      ) {
        validationErrors.push({
          type: ConfigValidationErrorTypes.ConfigArrayNumberOfElementsError,
          message:
            `${props.name}: too few (${props.elements?.length || 0}), ` +
            `must be at least ${props.minElements}.`,
        });
      }
    }
    if (props.maxElements) {
      // If maxElements was specified
      if (props.elements != null && props.elements.length > props.maxElements) {
        validationErrors.push({
          type: ConfigValidationErrorTypes.ConfigArrayNumberOfElementsError,
          message:
            `${props.name}: too many (${props.elements.length}), ` +
            `must be no more than ${props.maxElements}.`,
        });
      }
    }
  }
  if (props.elements != null) {
    const visitedNames: Array<string> = [];
    const duplicateNames: Set<string> = new Set();
    for (const e of props.elements) {
      if (visitedNames.includes(e.name)) {
        duplicateNames.add(`'${e.name}'`);
      } else {
        visitedNames.push(e.name);
      }
    }
    if (duplicateNames.size > 0) {
      validationErrors.push({
        type: ConfigValidationErrorTypes.DuplicateArrayNameError,
        message: `${props.name} array contains duplicate element names ${Array.from(duplicateNames).join(
          ', '
        )}`,
      });
    }
  }
  if (props.requiredConfig && props.value == null) {
    validationErrors.push({
      type: ConfigValidationErrorTypes.RequiredConfigError,
      message: `${props.name} is required.`,
    });
  }
  return validationErrors;
}

export default function BenchlingAppEditor({
  type,
  subtype,
  name,
  placeholder,
  value,
  requiredConfig,
  onChangeValue,
  onChangeElements,
  dependencyValidation,
  updateDependencyConfigValidation,
  updateArrayElementValidation,
  isEditable,
  schemaId,
  elementDefinition,
  minElements = null,
  maxElements = null,
  scalarEnum = null,
  elements,
}: BenchlingAppEditorProps): JSX.Element {
  useEffect(() => {
    const prevErrors = dependencyValidation.errors;
    const errors = getValidationErrors({
      name,
      type,
      value,
      requiredConfig,
      elements,
      minElements,
      maxElements,
    });

    if (!equal(prevErrors, errors)) {
      const newDependencyValidation = deepCopy(dependencyValidation);
      newDependencyValidation.errors = errors;
      updateDependencyConfigValidation(newDependencyValidation);
    }
  }, [
    dependencyValidation,
    name,
    type,
    value,
    requiredConfig,
    elements,
    minElements,
    maxElements,
    scalarEnum,
    updateDependencyConfigValidation,
  ]);

  switch (type) {
    case BenchlingAppType.array:
      if (elementDefinition != null) {
        return (
          <BenchlingAppConfigurationArrayRow
            arrayName={name}
            elementDefinition={elementDefinition}
            elements={elements || []}
            minElements={minElements}
            maxElements={maxElements}
            onChangeElements={onChangeElements}
            updateArrayElementValidation={updateArrayElementValidation}
          />
        );
      }
      return <></>;
    case BenchlingAppType.dropdown:
      return (
        <SchemaFieldSelectorInput
          onChange={onChangeValue}
          value={getStringValueForDependency(value)}
          disabled={!isEditable}
        />
      );
    case BenchlingAppType.plate_schema:
      return (
        <PlateSchemaSelector
          onChange={(schema: StorableSchema | null) => {
            onChangeValue(schema?.id ?? null);
          }}
          selectedPlateSchemaId={getStringValueForDependency(value)}
          shouldShowNoSchemaOption={true}
        />
      );
    case BenchlingAppType.container_schema:
      return (
        <ContainerSchemaSelector
          onChange={(schema) => {
            onChangeValue(schema?.id ?? null);
          }}
          selectedContainerSchemaId={getStringValueForDependency(value)}
          shouldShowNoSchemaOption={true}
        />
      );
    case BenchlingAppType.box_schema:
      return (
        <BoxSchemaSelector
          onChange={(schema: StorableSchema | null) => {
            onChangeValue(schema?.id ?? null);
          }}
          selectedBoxSchemaId={getStringValueForDependency(value)}
          shouldShowNoSchemaOption={true}
        />
      );
    case BenchlingAppType.location_schema:
      return (
        <LocationSchemaSelector
          onChange={(schema: StorableSchema | null) => {
            onChangeValue(schema?.id ?? null);
          }}
          selectedLocationSchemaId={getStringValueForDependency(value)}
          shouldShowNoSchemaOption={true}
        />
      );
    case BenchlingAppType.run_schema:
      return (
        <AssayRunSchemaSelectorIdInput
          onAssayRunSchemaChange={(schema) => {
            onChangeValue(schema?.id ?? null);
          }}
          selectedAssayRunSchemaId={getStringValueForDependency(value)}
          showPreview={false}
          shouldShowNoSchemaOption={true}
        />
      );
    case BenchlingAppType.request_schema:
      return (
        <RequestSchemaSelectorIdInput
          selectedRequestSchemaId={getStringValueForDependency(value)}
          isRequestSchemaValid={() => true}
          onRequestSchemaChange={(schema) => {
            onChangeValue(schema?.id ?? null);
          }}
          shouldShowNoSchemaOption={true}
        />
      );
    case BenchlingAppType.result_schema:
      return (
        <ResultSchemaSelectorIdInput
          selectedSchemaId={getStringValueForDependency(value)}
          isDisabled={!isEditable}
          onChange={(newSelectedSchema) => {
            onChangeValue(newSelectedSchema ? newSelectedSchema.id : null);
          }}
          shouldShowNoSchemaOption={true}
        />
      );
    case BenchlingAppType.entry_schema:
    case BenchlingAppType.entity_schema: {
      let folderItemTypes: Array<OligoAwareFolderItemType> | null = null;
      if (type === BenchlingAppType.entry_schema) {
        folderItemTypes = [ModelTypeConstants.ENTRY];
      } else if (subtype) {
        folderItemTypes = [subtypeToModelTypeMapping[subtype]];
      } else {
        folderItemTypes = getRegistrySupportedOligoAwareTypes();
      }
      return (
        <TagSchemaSelector
          oligoAwareFolderItemTypes={folderItemTypes}
          onChange={(schema) => {
            onChangeValue(schema ? schema.id : null);
          }}
          selectedTagSchemaId={getStringValueForDependency(value)}
          allowNone={true}
        />
      );
    }
    case BenchlingAppType.workflow_task_schema:
      return (
        <AppConfigurationProcessTaskSchemaSelector
          onChange={onChangeValue}
          selectedProcessTaskSchemaId={getStringValueForDependency(value)}
          disabled={!isEditable}
        />
      );
    case BenchlingAppType.registry:
      return (
        <RegistrySelector
          registryId={getStringValueForDependency(value)}
          onChange={(e) => {
            onChangeValue(e.target.value?.id ?? null);
          }}
        />
      );
    case BenchlingAppType.folder:
      return (
        <FolderSelector
          allowNone={true}
          level="read"
          value={getStringValueForDependency(value)}
          showInventory={true}
          onChange={(e) => {
            onChangeValue(e.target.value);
          }}
          readOnly={!isEditable}
          unsetOnFetchFail={true}
        />
      );
    case BenchlingAppType.entry:
      return (
        <WrappedEntrySearchInput
          autoFocus={false}
          id={getStringValueForDependency(value)}
          onChange={onChangeValue}
          disabled={!isEditable}
        />
      );
    case BenchlingAppType.worklist:
      return (
        <WorklistMultiSearchInput
          disabled={!isEditable}
          autoFocus={false}
          selectedItems={value ? [{id: String(value)}] : []}
          onChange={(worklists) => {
            if (worklists) {
              onChangeValue(worklists.length === 0 ? null : worklists[0].id);
            }
          }}
          // Search over all worklistTypes
          worklistTypes={Object.values(WorklistType)}
          shouldAllowMultiple={false}
          placeholder={placeholder}
        />
      );
    case BenchlingAppType.project:
      // Does not appear to support isEditable
      return (
        <ProjectSelector
          allowNone={true}
          enableRedesign={true}
          level="read"
          onProjectSelected={onChangeValue}
          selectedProjectId={getStringValueForDependency(value)}
        />
      );
    // Process Management
    // TODO: Support PROCESS_OUTPUT when ProcessManagementSearchInput supports it
    // case BenchlingAppType.PROCESS_TASK:
    case BenchlingAppType.workflow_task_status: {
      return (
        <AppConfigurationProcessTaskStatusSelector
          onChange={onChangeValue}
          selectedTaskStatusId={getStringValueForDependency(value)}
          disabled={!isEditable}
        />
      );
    }

    // BIOENTITIES
    case BenchlingAppType.dna_sequence:
    case BenchlingAppType.dna_oligo:
    case BenchlingAppType.aa_sequence:
    case BenchlingAppType.custom_entity:
    case BenchlingAppType.mixture:
    case BenchlingAppType.molecule:
    case BenchlingAppType.rna_oligo:
    case BenchlingAppType.rna_sequence:
      return (
        <WrappedBioentitySearchInput
          disabled={!isEditable}
          autoFocus={false}
          id={getStringValueForDependency(value)}
          onChange={onChangeValue}
          tagSchemaOrInterfaceId={schemaId}
          bioentityTypes={[MODEL_CONSTANT_BY_APP_CONFIG_TYPE[type]]}
          placeholderText={placeholder}
        />
      );

    // STORABLES
    case BenchlingAppType.box:
    case BenchlingAppType.container:
    case BenchlingAppType.location:
    case BenchlingAppType.plate:
      return (
        <WrappedStorableSearchInput
          disabled={!isEditable}
          storableTypes={[type]}
          {...(schemaId && {storableSchemaId: schemaId})}
          autoFocus={false}
          id={getStringValueForDependency(value)}
          onChange={onChangeValue}
          placeholder={placeholder}
        />
      );

    // SCALARS
    case BenchlingAppType.boolean:
      return (
        <CheckboxInput
          disabled={!isEditable}
          id={uniqueId('AppConfigurationCheckboxInput')}
          checked={(value as ScalarDependencyTypeToValueTSType[BenchlingAppType.boolean]) ?? false}
          onChange={(e: InputChangeEvent) => {
            onChangeValue(e.target.checked);
          }}
          label=""
        />
      );
    case BenchlingAppType.float:
      if (scalarEnum) {
        return (
          <ScalarEnumSelectorInput
            value={value as ScalarDependencyTypeToValueTSType[BenchlingAppType.text]}
            isEditable={isEditable}
            onChange={onChangeValue}
            scalarEnum={scalarEnum}
            requiredConfig={requiredConfig}
          />
        );
      }
      return (
        <IntermediateValueControlledNumericInput
          isDisabled={!isEditable}
          required={false}
          validateInteger={false}
          value={value as ScalarDependencyTypeToValueTSType[BenchlingAppType.float]}
          onChange={(e: NumericInputChangeEvent) => {
            if (e.target.isValid) {
              onChangeValue(e.target.numericValue);
            } else {
              onChangeValue(e.target.value);
            }
          }}
        />
      );
    case BenchlingAppType.integer:
      if (scalarEnum) {
        return (
          <ScalarEnumSelectorInput
            value={value as ScalarDependencyTypeToValueTSType[BenchlingAppType.text]}
            isEditable={isEditable}
            onChange={onChangeValue}
            scalarEnum={scalarEnum}
            requiredConfig={requiredConfig}
          />
        );
      }
      return (
        <IntermediateValueControlledNumericInput
          max={MAX_INT}
          min={MIN_INT}
          isDisabled={!isEditable}
          required={false}
          validateInteger={true}
          value={value as ScalarDependencyTypeToValueTSType[BenchlingAppType.integer]}
          onChange={(e: NumericInputChangeEvent) => {
            if (e.target.isValid) {
              onChangeValue(e.target.numericValue);
            } else {
              onChangeValue(e.target.value);
            }
          }}
        />
      );
    case BenchlingAppType.date:
      // Does not appear to support isEditable
      return (
        <DateInput
          value={coerceNullToEmptyStringForDisplay(
            value as ScalarDependencyTypeToValueTSType[BenchlingAppType.date]
          )}
          onChange={(newDateValue) => {
            onChangeValue(coerceEmptyStringToNullForSave(newDateValue));
          }}
        />
      );
    case BenchlingAppType.datetime:
      return (
        // Does not appear to support isEditable
        <DatetimeInput
          value={coerceNullToEmptyStringForDisplay(
            value as ScalarDependencyTypeToValueTSType[BenchlingAppType.datetime]
          )}
          onChange={(newDateValue) => {
            onChangeValue(coerceEmptyStringToNullForSave(newDateValue));
          }}
          placeholder=""
          autoFocus={false}
          validDatetimeInputFormats={DATETIME_FORMATS}
          datetimePresentationalFormat={PRESENTATIONAL_LOCAL_DATETIME_FORMAT}
          timeInputPlaceholder="hh=mm AM/PM"
          timeInformalInputFormats={DATEPICKER_CUSTOM_INPUT_USER_EDIT_INFORMAL_TIME_FORMATS}
          timeFormalInputFormats={DATEPICKER_CUSTOM_INPUT_USER_EDIT_FORMAL_TIME_FORMATS}
          timePresentationalFormat={PRESENTATIONAL_TIME_FORMAT}
        />
      );
    case BenchlingAppType.json:
      return (
        <AceEditor
          readOnly={!isEditable}
          mode="json"
          onChange={(value: string) => {
            onChangeValue(value);
          }}
          value={coerceNullToEmptyStringForDisplay(
            value as ScalarDependencyTypeToValueTSType[BenchlingAppType.json]
          )}
          editorProps={{$blockScrolling: true}}
        />
      );
    case BenchlingAppType.secure_text:
      return (
        <SecureTextInput
          disabled={!isEditable}
          onChange={(newValue: string) => {
            onChangeValue(newValue);
          }}
          name={name}
          description="After saving configurations this will no longer be visible."
          value={coerceNullToEmptyStringForDisplay(
            value as ScalarDependencyTypeToValueTSType[BenchlingAppType.secure_text]
          )}
        />
      );
    case BenchlingAppType.text:
    default:
      if (scalarEnum) {
        return (
          <ScalarEnumSelectorInput
            value={value as ScalarDependencyTypeToValueTSType[BenchlingAppType.text]}
            isEditable={isEditable}
            onChange={onChangeValue}
            scalarEnum={scalarEnum}
            requiredConfig={requiredConfig}
          />
        );
      }
      return (
        <TextInput
          disabled={!isEditable}
          onChange={(e: InputChangeEvent) => {
            onChangeValue(coerceEmptyStringToNullForSave(e.target.value));
          }}
          value={coerceNullToEmptyStringForDisplay(
            value as ScalarDependencyTypeToValueTSType[BenchlingAppType.text]
          )}
          dataTestAttrs={{component: 'BenchlingAppEditor', element: 'TextInput', key: name}}
        />
      );
  }
}
